/*
 *  -----------------------------------------------------------------------
 *    <copyright file="edit-overview-tables/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import WarningLostDataDialog from '@components-clean/legacy/unsaved-changes-dialog/warning-lost-data-dialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { createContext, useState } from 'react';
import { EditOverviewTablesProps } from '@components/admin/studies/study-settings-dialogs/edit-overview-tables/edit-overview-tables.interface';
import EditRiskOverviewConfig from '@components/admin/studies/study-settings-dialogs/edit-overview-tables/edit-risk-overview-config';
import { CustomDialog, DialogContent, DialogTitle } from '@components/core/dialog';
import OpraTabs from '@components/core/opra-tabs';
import { IOpraTab } from '@components/core/opra-tabs/opra-tabs.interface';
import EditStudySiteOverviewConfig from 'views/components/admin/studies/study-settings-dialogs/edit-overview-tables/edit-study-site-overview-config';
import { useBoolean, useOverviewTabs, useTranslation } from '@hooks/index';

import './styles.scss';

export const TabsContext = createContext<any>(null);

const EditOverviewTables = ({ study, canEdit, onClose }: EditOverviewTablesProps) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(0);
    const [nextTab, setNextTab] = useState<number | null>(null);
    const hasChangesDialog = useBoolean(false);

    const contextValue = useOverviewTabs();

    const tabsData: IOpraTab[] = [
        {
            tabTitle: t('admin:RISK'),
            tabContent: <EditRiskOverviewConfig study={study} canEdit={canEdit} activeTab={activeTab} />,
        },
        {
            tabTitle: t('admin:STUDY'),
            tabContent: <EditStudySiteOverviewConfig study={study} canEdit={canEdit} activeTab={activeTab} isStudy />,
        },
        {
            tabTitle: t('admin:SITE'),
            tabContent: <EditStudySiteOverviewConfig study={study} canEdit={canEdit} activeTab={activeTab} />,
        },
    ];

    const onCloseDialog = () => {
        const isHasChanges = contextValue.getChangesContain();
        if (!isHasChanges) {
            contextValue.setChangesContain(false);
            onClose();
        } else {
            hasChangesDialog.setTrue();
            setNextTab(null);
        }
    };

    return (
        <CustomDialog
            className="overview-tables-dialog full-screen-dialog"
            onClose={onClose}
            aria-labelledby="overview-tables"
            fullWidth
            scroll="paper"
            fullScreen
            open
        >
            {hasChangesDialog.value && (
                <WarningLostDataDialog
                    open={hasChangesDialog.value}
                    onClose={(action: string) => {
                        if (action === 'close') {
                            if (nextTab !== null) {
                                setActiveTab(nextTab);
                            } else {
                                onClose();
                            }
                            contextValue.setChangesContain(false);
                        }
                        hasChangesDialog.setFalse();
                    }}
                />
            )}
            <DialogTitle id="overview-tables-title">
                {t('admin:editOverviewTables')}
                <FontAwesomeIcon
                    id="study-close-button"
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => onCloseDialog()}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="dialog-content overview-tables-dialog__content">
                <TabsContext.Provider value={contextValue}>
                    <OpraTabs
                        data={tabsData}
                        activeTab={activeTab}
                        setActiveTab={(numberTab: number) => {
                            const isHasChanges = contextValue.getChangesContain();

                            if (!isHasChanges) {
                                setActiveTab(numberTab);
                                contextValue.setChangesContain(false);
                            } else {
                                hasChangesDialog.setTrue();
                                setNextTab(numberTab);
                            }
                        }}
                        className="opra-tabs--wide-content opra-tabs--bg-white"
                    />
                </TabsContext.Provider>
            </DialogContent>
        </CustomDialog>
    );
};

export default EditOverviewTables;
